import React, { Component } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { Tooltip } from '@knockrentals/knock-shared-web';
import { AuthenticationService, Toaster } from '@knockrentals/knock-react';
import { GroupUserDelete, GroupUserDeleteModal } from './GroupUserDeleteModal';
import GroupUserEditor from './GroupUserEditor';
import IntegrationAPI from '../Integrations/IntegrationsAPI';
import TeamEditor from './TeamEditor';
import TransferTeam, { getTransferTeamErrorMessage } from './TransferTeam';
import { UserIntegrations } from './UserIntegrations';
import LoginAPI from '../Login/LoginAPI';
import UsersAPI from './UsersAPI';
import { FeatureFlagContext } from '../../Context/FeatureFlagContext';
import { UNIFIED_LOGIN_URL } from '../../config';
import { IntegrationVendorNames } from '../Integrations/constants';
import { TIMEZONES } from '../../constants';

class GroupUsers extends Component {
  static VendorMapping = {
    realpage: IntegrationVendorNames.REALPAGE,
    resman: IntegrationVendorNames.RESMAN,
    yardi: IntegrationVendorNames.YARDI,
    entrata: IntegrationVendorNames.ENTRATA,
  };

  state = {
    confirmDeleteGroupUser: false,
    deletingUser: {},
    deletionsInProgress: JSON.parse(
      sessionStorage.getItem('deletionsInProgress'),
    ),
    editingTeam: false,
    integrationsSynchronizing: {},
    isAddingUser: false,
    isSaving: false,
    loggedInUserType: AuthenticationService.getRole(),
    transferringTeam: null,
    transferringTeamError: null,
    transferringTeamInProgress: false,
    selectedUser: null,
    selectingTransfer: false,
  };

  _isMounted = false;

  static contextType = FeatureFlagContext;

  async componentDidMount() {
    this._isMounted = true;
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  useCookieForToken = () => {
    const { isUseCookieForTokenEnabled } = this.context;
    return isUseCookieForTokenEnabled;
  };

  userDeleteDisabled = () => {
    const isInternalMode = AuthenticationService._internalMode;
    return isInternalMode ? false : this.context.isUserDeleteDisabled;
  };

  closeTransferTeamModal = () => {
    this.setState({
      transferringTeamError: '',
      transferringTeam: false,
    });
  };

  clearTransferError = () => {
    if (this.state.transferringTeamError) {
      this.setState({ transferringTeamError: '' });
    }
  };

  submitTransferTeam(transferMasterUsername) {
    this.setState({
      transferringTeamInProgress: true,
      transferringTeamError: '',
    });

    const thisLeasingTeamId = this.props.group.leasing_team_id;

    return UsersAPI.transferTeam(thisLeasingTeamId, transferMasterUsername)
      .then(() => {
        Toaster.showToast('Transferred!', 2000, Toaster.ToastClasses.success);

        this.setState({
          transferringTeam: false,
          transferringTeamInProgress: false,
          transferringTeamError: '',
        });
      })
      .catch((error) => {
        const errorMessage = getTransferTeamErrorMessage(error.message);
        this.setState({
          transferringTeamError: errorMessage,
          transferringTeamInProgress: false,
        });
      });
  }

  render() {
    const leasingTeamName = this.props.group.leasing_team_name;

    return (
      <div>
        <h2>
          {leasingTeamName} {`(ID: ${this.props.group.leasing_team_public_id})`}
          {AuthenticationService._internalMode ? (
            <button
              className="knock-react-button"
              onClick={() => {
                this.setState({ editingTeam: true });
              }}
            >
              Edit team
            </button>
          ) : null}
          {AuthenticationService._internalMode ? (
            <button
              className="knock-react-button"
              onClick={() => {
                this.setState({ transferringTeam: true });
              }}
            >
              Transfer team
            </button>
          ) : null}
        </h2>
        {this.renderTable()}

        {!this.state.isAddingUser ? (
          <button
            className="knock-react-button"
            onClick={this.addUser.bind(this)}
          >
            Add new user
          </button>
        ) : null}

        {this.state.editingTeam ? (
          <TeamEditor
            team={this.props.group}
            onUpdateTeam={this.onUpdateTeam.bind(this)}
            onCancel={() => {
              this.setState({ editingTeam: false });
            }}
          />
        ) : null}

        {(this.state.selectedUser || this.state.isAddingUser) && (
          <GroupUserEditor
            group={this.props.group}
            groups={this.props.groups}
            integrationAgentsMap={this.props.integrationAgentsMap}
            isSaving={this.state.isSaving}
            isUserManagementEnabled={this.props.isUserManagementEnabled}
            onCancel={this.onCancelEdit.bind(this)}
            onUserCreated={this.onUserCreated.bind(this)}
            onUserPasswordUpdated={this.onUserPasswordUpdated.bind(this)}
            onUserUpdated={this.onUserUpdated.bind(this)}
            setIsUserManagementEnabled={this.props.setIsUserManagementEnabled}
            user={this.state.selectedUser}
            vendorMapping={GroupUsers.VendorMapping}
          />
        )}

        {this.state.transferringTeam && (
          <TransferTeam
            clearError={this.clearTransferError}
            leasingTeamName={leasingTeamName}
            onCancel={this.closeTransferTeamModal}
            onSubmit={this.submitTransferTeam.bind(this)}
            transferError={this.state.transferringTeamError}
            transferringTeamInProgress={this.state.transferringTeamInProgress}
          />
        )}

        {this.state.deletingUser.user_id &&
          (this.context.isAdminAssignedManagerEnabled ? (
            <GroupUserDelete
              onCancel={this.cancelDeletingUser}
              onDelete={this.deleteUser}
              user={this.state.deletingUser}
            />
          ) : (
            <GroupUserDeleteModal
              eligibleTransferRecipients={this.eligibleTransferRecipients()}
              onCancel={this.cancelDeletingUser}
              onDelete={this.deleteUser}
              user={this.state.deletingUser}
            />
          ))}
      </div>
    );
  }

  renderTable() {
    if (this.props.group.users.length === 0) {
      return (
        <div>
          <span>There are no users for this leasing team.</span>
        </div>
      );
    }

    return (
      <table className="wire-frame-table group-users-table">
        <thead>
          <tr>
            <th>Name</th>
            <th>Username</th>
            <th>Contact Email</th>
            <th>Phone</th>
            <th>Timezone</th>
            {this.renderIntegrationHeaders()}
            <th />
            <th>
              {this.context.isAdminAssignedManagerEnabled
                ? 'Owner Properties'
                : 'House?'}
            </th>
            <th>Last Login On</th>
            <th>User Migrated to RealPage Unified Platform</th>
          </tr>
        </thead>
        <tbody>{this.renderUsers()}</tbody>
      </table>
    );
  }

  synchronizeIntegration(integration) {
    const integrationsSynchronizing = this.state.integrationsSynchronizing;

    integrationsSynchronizing[integration.vendor_property_id] = true;

    this.setState({ integrationsSynchronizing });

    return IntegrationAPI.synchronizeIntegration(
      integration.property_id,
      integration.vendor_property_id,
      integration.vendor,
    )
      .then(() => {
        window.location.reload();
      })
      .finally(() => {
        const integrationsSynchronizing = this.state.integrationsSynchronizing;

        integrationsSynchronizing[integration.vendor_property_id] = false;

        this.setState({ integrationsSynchronizing });
      });
  }

  renderIntegrationHeaders() {
    return this.props.group.integrations
      .filter((integration) => integration !== null)
      .map((integration) => {
        const integrationKey =
          integration.vendor +
          '-' +
          integration.property_id +
          '-' +
          integration.vendor_property_id;

        const isSynchronizing =
          this.state.integrationsSynchronizing[integration.vendor_property_id];
        const clickableClass = isSynchronizing
          ? 'fa fa-refresh fa-fw fa-spin'
          : 'fa fa-refresh fa-fw';

        const integrationSyncClickable = (
          <a
            href="#"
            onClick={() => {
              this.synchronizeIntegration(integration);
            }}
          >
            <i className={clickableClass} />
          </a>
        );
        const integrationLabel = (
          <span>
            {' ( ID: ' + integration.vendor_property_id + ') '}{' '}
            {integrationSyncClickable}{' '}
          </span>
        );

        return (
          <th key={integrationKey}>
            {GroupUsers.VendorMapping[integration.vendor]} Mapping{' '}
            {integrationLabel}{' '}
          </th>
        );
      });
  }

  isDeleteButtonDisabled(deletionInProgress, user, group) {
    return (
      deletionInProgress ||
      (!this.context.isAdminAssignedManagerEnabled && user.is_hub_account) ||
      group.owner_manager_ids.includes(user.user_id) ||
      user.is_migrated
    );
  }

  getDeleteButtonTooltip(user, group) {
    let tooltip = '';

    if (!this.context.isAdminAssignedManagerEnabled && user.is_hub_account) {
      tooltip =
        'This user must be unmarked as the house account before removal';
    }

    if (group.owner_manager_ids.includes(user.user_id)) {
      tooltip =
        "This user is the owning manager for a property and can't be removed";
    }

    if (user.is_migrated) {
      tooltip =
        'This user has been migrated. Delete button for this user has been disabled. Click here to modify user in Unified Platform';
    }

    return tooltip;
  }

  renderUsers() {
    return this.props.group.users.map((user) => {
      const editUser = () => {
        this.editUser(user);
      };

      const loginToUser = () => {
        this.loginToUser(user);
      };

      const deletionInProgress =
        this.state.deletionsInProgress &&
        Array.isArray(this.state.deletionsInProgress)
          ? this.state.deletionsInProgress.includes(user.user_id)
          : false;

      const deleteButtonDisabled = this.isDeleteButtonDisabled(
        deletionInProgress,
        user,
        this.props.group,
      );

      if (user.property_integration_mappings) {
        user.integrations = user.property_integration_mappings;
      }

      const mappedAgentOnChange = async (updatedIntegrations) => {
        const isUpdated = await this.onUserUpdated({
          ...user,
          integrations: updatedIntegrations,
        });
        return isUpdated || Promise.reject();
      };

      return (
        <tr key={user.user_id}>
          <td>
            {user.first_name} {user.last_name}
          </td>
          <td>
            {user.cognito_username ? (
              <span>
                {user.cognito_username} <small>(cognito)</small>
                <br />
              </span>
            ) : null}
            {user.username ? user.username : null}
          </td>
          <td>{user.email}</td>
          <td>{user.phone_number}</td>
          <td>{this.renderTimezoneDisplayName(user)}</td>

          <UserIntegrations
            integrationAgentsMap={this.props.integrationAgentsMap}
            leasingTeamIntegrations={this.props.group.integrations}
            mappedAgentOnChange={mappedAgentOnChange}
            userIntegrations={user.integrations || []}
          />

          <td>
            {this.state.deletingUser.user_id !== user.user_id && (
              <div>
                <button className="knock-react-button" onClick={editUser}>
                  Edit
                </button>
                {user.is_migrated && (
                  <Tooltip title="This user has been migrated. Certain properties for this user cannot be edited. Click here to modify user in Unified Platform">
                    <a
                      href={UNIFIED_LOGIN_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <i className="fa-regular fa-question-circle edit-button-tooltip"></i>
                    </a>
                  </Tooltip>
                )}
                <button className="knock-react-button" onClick={loginToUser}>
                  Login
                </button>
                {this.props.group.users.length > 1 &&
                  !this.userDeleteDisabled() && (
                    <React.Fragment>
                      <button
                        className="knock-react-button danger"
                        disabled={deleteButtonDisabled}
                        onClick={this.startDeletingUser.bind(this, user)}
                      >
                        {deletionInProgress ? (
                          <i
                            className="fa fa-spinner fa-spin"
                            aria-hidden="true"
                          ></i>
                        ) : (
                          <span>Delete</span>
                        )}
                      </button>

                      {deleteButtonDisabled && (
                        <Tooltip
                          title={this.getDeleteButtonTooltip(
                            user,
                            this.props.group,
                          )}
                        >
                          {user.is_migrated ? (
                            <a
                              href={UNIFIED_LOGIN_URL}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <i className="fa-regular fa-question-circle delete-button-tooltip"></i>
                            </a>
                          ) : (
                            <i className="fa-regular fa-question-circle delete-button-tooltip"></i>
                          )}
                        </Tooltip>
                      )}
                    </React.Fragment>
                  )}
              </div>
            )}
          </td>
          {this.context.isAdminAssignedManagerEnabled ? (
            <td className="owned-properties-cell">
              {this.renderOwnedProperties(user)}
            </td>
          ) : (
            <td>
              <input
                type="checkbox"
                checked={user.is_hub_account}
                onChange={this.isHubChanged.bind(this, user)}
              />
            </td>
          )}
          <td>
            {user.last_login ? this.renderLastLogin(user.last_login) : null}
          </td>
          <td>{user.is_migrated ? 'migrated' : ''}</td>
        </tr>
      );
    });
  }

  renderLastLogin(lastLogin) {
    return moment(lastLogin).format('L [at] h:mm a');
  }

  isHubChanged(user, event) {
    user.is_hub_account = event.target.checked;

    this.onUserUpdated({
      ...user,
      username: user.cognito_username || user.username,
    });
  }

  /* renderUserTimezones is deprecated due to issues
    editing timezones for multi-team users via the dropdown;
    will be removed in the future once we're certain we won't need to re-enable it
   */
  renderUserTimezones(user) {
    const userTimezoneChanged = (event) => {
      user.timezone = event.target.value;
      user.currentLeasingTeamIds = [this.props.group.leasing_team_id];
      UsersAPI.updateGroupUser(user.user_id, user, user.integrations)
        .then(() => {
          Toaster.showToast('Saved!', 2000, Toaster.ToastClasses.success);
        })
        .catch(() => {
          Toaster.showToast(
            'Error saving timezone',
            2000,
            Toaster.ToastClasses.error,
          );
        });

      this.setState({ value: user.timezone });
    };

    return (
      <td>
        <select value={user.timezone || null} onChange={userTimezoneChanged}>
          {user.timezone === null ? (
            <option value={null}>--Select--</option>
          ) : null}
          {GroupUsers.renderTimezoneOptions()}
        </select>
      </td>
    );
  }

  /* renderTimezoneOptions is also deprecated due to issues
    editing timezones for multi-team users
   */
  static renderTimezoneOptions() {
    return TIMEZONES.map((timezone) => (
      <option key={timezone.id} value={timezone.id}>
        {timezone.name}
      </option>
    ));
  }

  renderOwnedProperties(user) {
    if (!user.owned_properties?.length) {
      return '—';
    }
    return (
      <div className="owned-properties-list">
        {user.owned_properties.map((property) => (
          <div key={property.property_id}>{property.property_name}</div>
        ))}
      </div>
    );
  }

  renderTimezoneDisplayName(user) {
    const displayTimezone = TIMEZONES.find(
      (tz) => tz.id === user.timezone,
    );
    if (displayTimezone && displayTimezone.name) return displayTimezone.name;
    return 'None Selected';
  }

  editUser(selectedUser) {
    this.setState({ selectedUser, isAddingUser: false });
  }

  loginToUser(selectedUser) {
    const useCookie = this.useCookieForToken();

    if (useCookie) {
      LoginAPI.openWebappAsUserFromCookie(selectedUser.user_id);
    } else {
      LoginAPI.openWebappAsUser(selectedUser.user_id);
    }
  }

  addUser() {
    this.setState({ selectedUser: null, isAddingUser: true });
  }

  onUpdateTeam(newName) {
    this.props.onUpdateTeam(this.props.group.leasing_team_id, newName);
    this.setState({ editingTeam: false });
  }

  onUserUpdated(user) {
    const integrations = user.integrations.map((integration) => {
      if (
        !integration.mapped_agent_id ||
        integration.mapped_agent_id === 'None'
      ) {
        return {
          ...integration,
          mapped_agent_id: null,
        };
      }

      return integration;
    });
    this.setState({ isSaving: true });
    return this.props
      .onUserUpdated({
        ...user,
        integrations,
      })
      .then((wasUpdated) => {
        this.setState({ isSaving: false });
        if (wasUpdated) {
          this.onCancelEdit();
        }
        return wasUpdated;
      });
  }

  onUserCreated(user) {
    this.setState({ isSaving: true });
    this.props.onUserCreated(user).then((wasCreated) => {
      this.setState({ isSaving: false });
      if (wasCreated) {
        this.onCancelEdit();
      }
    });
  }

  onUserPasswordUpdated(userId, password) {
    this.setState({ isSaving: true });
    this.props.onUserPasswordUpdated(userId, password).then(() => {
      this.setState({ isSaving: false });
      this.onCancelEdit();
    });
  }

  onCancelEdit() {
    if (this._isMounted) {
      this.setState({ selectedUser: null, isAddingUser: false });
    }
  }

  startDeletingUser(deletingUser) {
    this.setState({ deletingUser });
  }

  cancelDeletingUser = () => {
    this.setState({ deletingUser: {} });
  };

  deleteUser = (deletingUserId, transferToUserId) => {
    this.props.onUserDeleted(deletingUserId, transferToUserId);

    this.setState((prevState) => ({
      deletionsInProgress: [
        deletingUserId,
        ...(prevState.deletionsInProgress || []),
      ],
      deletingUser: {},
    }));
  };

  eligibleTransferRecipients = () => {
    const { deletingUser } = this.state;
    const deletionsInProgress = this.state.deletionsInProgress || [];

    return this.props.group.users.filter(
      (user) =>
        user.user_id !== deletingUser.user_id &&
        !deletionsInProgress.includes(user.user_id),
    );
  };
}

export default GroupUsers;

const integrationShape = PropTypes.shape({
  property_id: PropTypes.string,
  vendor_property_id: PropTypes.string,
  vendor: PropTypes.string,
});

const userIntegrationShape = PropTypes.shape({
  mapped_agent_id: PropTypes.string,
  property_id: PropTypes.string,
  vendor_property_id: PropTypes.string,
  vendor: PropTypes.string,
});

const userShape = PropTypes.shape({
  user_id: PropTypes.string,
  first_name: PropTypes.string,
  last_name: PropTypes.string,
  email: PropTypes.string,
  phone_number: PropTypes.string,
  timezone: PropTypes.string,
  is_hub_account: PropTypes.bool,
  is_migrated: PropTypes.bool,
  last_login: PropTypes.string,
  cognito_username: PropTypes.string,
  username: PropTypes.string,
  integrations: PropTypes.arrayOf(userIntegrationShape),
  property_integration_mappings: PropTypes.arrayOf(userIntegrationShape),
});

const groupShape = PropTypes.shape({
  leasing_team_id: PropTypes.string,
  leasing_team_name: PropTypes.string,
  leasing_team_public_id: PropTypes.string,
  owner_manager_ids: PropTypes.arrayOf(PropTypes.string),
  users: PropTypes.arrayOf(userShape),
  integrations: PropTypes.arrayOf(integrationShape),
});

GroupUsers.propTypes = {
  group: groupShape,
  groups: PropTypes.arrayOf(groupShape),
  integrationAgentsMap: PropTypes.objectOf(
    PropTypes.shape({
      agent_id: PropTypes.string,
      leasing_team_id: PropTypes.string,
      property_id: PropTypes.string,
      vendor_property_id: PropTypes.string,
      vendor: PropTypes.string,
    }),
  ),
  isUserManagementEnabled: PropTypes.bool,
  onUpdateTeam: PropTypes.func,
  onUserUpdated: PropTypes.func,
  onUserCreated: PropTypes.func,
  onUserDeleted: PropTypes.func,
  onUserPasswordUpdated: PropTypes.func,
  setIsUserManagementEnabled: PropTypes.func,
};
